import React from "react";
import { Button, Space, Slider, Tooltip } from "antd";
import {
  ZoomInOutlined,
  LinkOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import { LuAxis3D } from "react-icons/lu";
import { RiCrosshair2Line } from "react-icons/ri";
import { IoIosMove } from "react-icons/io";
import { MdLayers } from "react-icons/md";
import AnnotationToolPicker from "./AnnotationToolPicker";

const ACTIVE_COLOR = "#5dade2";

const RadiologyToolbar = ({
  activeTool,
  onToolClick,
  isMPRView = false,
  isSynced = false,
  syncCompatible = true,
  totalSlices = 0,
  onSyncToggle,
  onMprClick,
  crosshairsToolName,
  syncDisabled = false,
  mprDisabled = false,
  syncTooltipText,
  isFullscreen = false,
  onFullscreenToggle,
  isMip = false,
  mipDisabled = false,
  mipTooltipText,
  onMipToggle,
  slabThickness = 5,
  slabThicknessMax = 100,
  onSlabThicknessChange,
  annotationShape = "rectangle",
  isAnnotating = false,
  onAnnotationShapePick,
  showAnnotationPicker = true,
  style,
}) => {
  const getSyncTooltip = () => {
    if (syncTooltipText) return syncTooltipText;
    if (totalSlices <= 1) return "Examen non synchronisable (vue unique)";
    if (!syncCompatible) return "Cet examen ne peut pas être synchronisé avec les examens actifs";
    return isSynced ? "Désactiver la synchronisation" : "Synchroniser l'examen";
  };

  return (
    <div style={style}>
      <Space direction="vertical">
        <Tooltip title="Déplacer" placement="right">
          <Button
            type="primary"
            shape="circle"
            size="middle"
            className="viewer-tool-btn"
            icon={<IoIosMove />}
            onClick={() => onToolClick("Pan")}
            style={{ backgroundColor: activeTool === "Pan" ? ACTIVE_COLOR : undefined }}
          />
        </Tooltip>
        <Tooltip title="Zoom" placement="right">
          <Button
            type="primary"
            shape="circle"
            size="middle"
            className="viewer-tool-btn"
            icon={<ZoomInOutlined />}
            onClick={() => onToolClick("Zoom")}
            style={{ backgroundColor: activeTool === "Zoom" ? ACTIVE_COLOR : undefined }}
          />
        </Tooltip>

        {!isMPRView && showAnnotationPicker && (
          <AnnotationToolPicker
            activeShape={annotationShape}
            isDrawing={isAnnotating}
            onPick={onAnnotationShapePick}
            placement="right"
            buttonClassName="viewer-tool-btn"
            excludeShapes={["freehand"]}
          />
        )}

        {!isMPRView && (
          <Tooltip placement="right" title={getSyncTooltip()}>
            <Button
              type="primary"
              shape="circle"
              size="middle"
              className="viewer-tool-btn"
              icon={<LinkOutlined style={syncDisabled ? { color: '#999999' } : undefined} />}
              onClick={onSyncToggle}
              disabled={syncDisabled || totalSlices <= 1 || isMPRView}
              style={{
                backgroundColor: syncDisabled ? '#444444' : (isSynced ? ACTIVE_COLOR : undefined),
                border: syncDisabled ? 'none' : undefined,
              }}
            />
          </Tooltip>
        )}

        <Tooltip title={mprDisabled ? "Vue MPR (indisponible)" : (isMPRView ? "Vue simple" : "Vue MPR")} placement="right">
          <Button
            type="primary"
            shape="circle"
            size="middle"
            className="viewer-tool-btn"
            icon={<LuAxis3D style={mprDisabled ? { color: '#999999' } : undefined} />}
            onClick={onMprClick}
            disabled={mprDisabled}
            style={{
              backgroundColor: mprDisabled ? '#444444' : (isMPRView ? ACTIVE_COLOR : undefined),
              border: mprDisabled ? 'none' : undefined,
            }}
          />
        </Tooltip>

        {isMPRView && crosshairsToolName && (
          <Tooltip title={activeTool === crosshairsToolName ? "Désactiver les réticules" : "Activer les réticules"} placement="right">
            <Button
              type="primary"
              shape="circle"
              size="middle"
              className="viewer-tool-btn"
              icon={<RiCrosshair2Line />}
              onClick={() => onToolClick(crosshairsToolName)}
              style={{ backgroundColor: activeTool === crosshairsToolName ? ACTIVE_COLOR : undefined }}
            />
          </Tooltip>
        )}

        {!isMPRView && onMipToggle && (
          <div className="viewer-tool-btn" style={{ position: 'relative' }}>
            <Tooltip
              placement="right"
              title={mipDisabled ? (mipTooltipText || "MIP non disponible") : (isMip ? "Désactiver le MIP" : "Activer le MIP")}
            >
              <Button
                type="primary"
                shape="circle"
                size="middle"
                icon={<MdLayers style={mipDisabled ? { color: '#999999' } : undefined} />}
                onClick={onMipToggle}
                disabled={mipDisabled}
                style={{
                  backgroundColor: mipDisabled ? '#444444' : (isMip ? ACTIVE_COLOR : undefined),
                  border: mipDisabled ? 'none' : undefined,
                }}
              />
            </Tooltip>

            {isMip && !mipDisabled && onSlabThicknessChange && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 40,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  padding: '8px 6px',
                  borderRadius: 4,
                  color: '#fff',
                  width: 44,
                }}
              >
                <div style={{ fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {Math.round(slabThickness)} mm
                </div>
                <Slider
                  vertical
                  min={1}
                  max={Math.max(1, Math.round(slabThicknessMax))}
                  value={slabThickness}
                  onChange={onSlabThicknessChange}
                  step={1}
                  tooltip={{ open: false }}
                  style={{ height: 160, margin: 0 }}
                />
              </div>
            )}
          </div>
        )}

        {onFullscreenToggle && (
          <Tooltip title={isFullscreen ? "Quitter le plein écran" : "Plein écran"} placement="right">
            <Button
              type="primary"
              shape="circle"
              size="middle"
              className="viewer-tool-btn"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={onFullscreenToggle}
            />
          </Tooltip>
        )}
      </Space>
    </div>
  );
};

export default RadiologyToolbar;
